import type { TokenCredential } from "@azure/identity";
import { INTAKE_AGENT_NAME } from "@m8t-stack/api-contract";
import { deployPromptAdvisor } from "./deploy-prompt-advisor.js";

export const INTAKE_PERSONA = "azure-advisor-intake";
/** Re-exported under the historical name so callers are unaffected. The single
 *  declared name for this agent lives in @m8t-stack/api-contract — see that
 *  module's doc comment for why it must not fork again. */
export const INTAKE_AGENT = INTAKE_AGENT_NAME;

/**
 * Deploy the no-brain onboarding intake agent (`azzy-intake`) from the intake persona.
 * Reads model + reasoning from the persona frontmatter; the body (placeholder-
 * free) becomes the instructions. ~10s via createPromptVersion. Idempotent in
 * the Foundry sense (each call = a new version).
 *
 * The intake persona declares three fillable-fields — `founder_identity_note`,
 * `chosen_model_note`, and `subscription_inventory_note`; `deployIntakeAgent`
 * forwards optional `fieldOverrides` (e.g. the founder's Azure identity and
 * chosen-model note, injected by `m8t bootstrap ui`) which are merged over
 * the persona defaults at deploy time.
 */
export async function deployIntakeAgent(args: {
  credential: TokenCredential;
  endpoint: string;
  repoRoot: string;
  /** Model chosen by the cascade. Omitted → the persona frontmatter floor. */
  model?: string;
  fieldOverrides?: Record<string, string>;
}): Promise<string> {
  try {
    return await deployPromptAdvisor({
      credential: args.credential,
      endpoint: args.endpoint,
      repoRoot: args.repoRoot,
      persona: INTAKE_PERSONA,
      agentName: INTAKE_AGENT,
      model: args.model,
      fieldOverrides: args.fieldOverrides,
    });
  } catch (e: unknown) {
    // Re-map ADVISOR_* codes to this module's own INTAKE_* codes, so callers
    // observe error codes named for the intake seam rather than the shared
    // deploy-prompt-advisor helper underneath it.
    if (e && typeof e === "object" && "code" in e) {
      const err = e as { code: string; message: string; hint?: string; cause?: unknown };
      if (err.code === "ADVISOR_PERSONA_MISSING") {
        const { LocalCliError } = await import("./errors.js");
        throw new LocalCliError({
          code: "INTAKE_PERSONA_MISSING",
          message: err.message,
          hint: err.hint,
          cause: err.cause,
        });
      }
      if (err.code === "ADVISOR_NO_MODEL") {
        const { LocalCliError } = await import("./errors.js");
        throw new LocalCliError({
          code: "INTAKE_NO_MODEL",
          message: err.message,
          hint: err.hint,
          cause: err.cause,
        });
      }
      if (err.code === "ADVISOR_BAD_EFFORT") {
        const { LocalCliError } = await import("./errors.js");
        throw new LocalCliError({
          code: "INTAKE_BAD_EFFORT",
          message: err.message,
          hint: err.hint,
          cause: err.cause,
        });
      }
    }
    throw e;
  }
}
